import React, { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from "react";
import { type MapDispatchToPropsParam, connect } from "react-redux";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { ClassNameMap } from "@material-ui/styles";
import {
    CircularProgress,
    Collapse,
    FormControl,
    InputAdornment,
    OutlinedInput,
    OutlinedInputClassKey,
    Popover,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import classnames from "classnames";
import _ from "lodash";

import { DATATEST } from "constants/integration";
import ChevronLeftIcon from "../../icons/chevron-left";
import ChevronRightIcon from "../../icons/chevron-right";
import { format } from "lib/globalize-util";
import { resetCustomizationCheckedImages, setSearching } from "actions/customization-actions";
import { IStore } from "reducers";
import { ICustomizationImageEntity } from "epics/customizations-epic";
import {
    ImagePermissionType,
    CustomizationImageFilterDictionary,
    IAttribute,
    CustomizationImageStatus,
} from "constants/product-customizations/customization";
import { TCustomizationSelection } from "constants/product-customizations/customization-selection.types";
import { ICustomizationImageTag } from "constants/product-customizations/customization-image-tag-schema";
import theme from "styles/theme";
import { useDebounce } from "hooks/useDebounce";
import { useSkipFirstEffect } from "hooks/useSkipFirstEffect";
import { useCustomizationModalContext } from "../context/customization-modal-context";
import { IProduct } from "records/item/item";
import { ICompany } from "../../contacts/contacts-types";
import { customizationImageEntitiesWithAttributes, getImageListFilters } from "./helpers";
import { DropShadow, AspectImage } from "./customization-image";

const EXISTING_IMAGES_PAGE_SIZE = 10;

export interface IStateProps {
    isFetchingExistingImages: boolean;
    imagesError?: string | Error;
    isUploading: boolean;
    company?: ICompany;
    tags: ICustomizationImageTag[];
    verifiedImages?: ICustomizationImageEntity[] | null;
    isChecking?: boolean;
    checkError?: string | Error;
    isTabChecking?: boolean;
}

export interface IProps {
    product: IProduct;
    attribute: IAttribute;
    selected?: TCustomizationSelection;
    tabType: ImagePermissionType;
    merchantId?: string;
    onChange: (image: ICustomizationImageEntity) => void;
    onSelectImage: (selectedImage: ICustomizationImageEntity | null) => void;
    onRemoveSelectedImage: () => void;
}

export interface IDispatchProps {
    onSearchingStatusChanged: (value: boolean) => void;
    onResetCheckedImages: () => void;
}
export type TImagesListProps = IProps & IDispatchProps & IStateProps;

export const ImagesListContainer: React.FC<TImagesListProps> = ({
    product,
    tabType,
    merchantId,
    selected,
    onSearchingStatusChanged,
    isFetchingExistingImages,
    imagesError,
    onChange,
    onSelectImage,
    isUploading,
    attribute,
    tags,
    company,
    isChecking,
    verifiedImages,
    onRemoveSelectedImage,
    onResetCheckedImages,
    isTabChecking,
}) => {
    const classes = useStyles();
    const searchInputClasses = useSearchInputStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [currentPageImages, setCurrentPageImages] = useState<ICustomizationImageEntity[]>([]);
    const [imageFilters, setImageFilters] = useState<CustomizationImageFilterDictionary | null>(null);
    const debouncedSearchValue = useDebounce<string | null>(searchValue, 500);
    const {
        currentTabCacheKey,
        existingImages: { images: contextImages },
        fetchImages,
        checkImages,
        getSearchString,
        handleTabChange,
        saveSearchString,
        resetImagesList,
        handleImageFiltersChange,
    } = useCustomizationModalContext();

    const schemaTags = useMemo(() => {
        if (!tags) {
            return;
        }

        return (tags as ICustomizationImageTag[]).filter(({ isArchived }) => !isArchived);
    }, [tags]);

    const schemaTagDictionary = useMemo(() => _.keyBy(schemaTags, "externalId"), [schemaTags]);

    const images = useMemo((): ICustomizationImageEntity[] => {
        return customizationImageEntitiesWithAttributes(contextImages, schemaTagDictionary);
    }, [contextImages, schemaTagDictionary]);

    const getImageFilters = useCallback((): CustomizationImageFilterDictionary | null => {
        const isBuyer = tabType === ImagePermissionType.BUYER;
        if (!schemaTags || isBuyer) {
            return null;
        }

        return getImageListFilters(schemaTags, attribute, selected, company, product);
    }, [tabType, company, product, selected, attribute, schemaTags]);

    useEffect(() => {
        const filters = getImageFilters();
        handleImageFiltersChange(filters);
        setImageFilters(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getImageFilters]);

    useSkipFirstEffect(() => {
        resetImagesList();
    }, [imageFilters]);

    const getFilteredImages = useCallback(() => {
        if (!selected?.imageAttributes?.length || isChecking || isTabChecking) {
            return;
        }

        checkImages(getMerchantId(tabType, merchantId), debouncedSearchValue, imageFilters, [selected.value]);
    }, [selected, isChecking, tabType, merchantId, debouncedSearchValue, imageFilters]);

    useEffect(() => {
        if (!imageFilters) {
            return;
        }

        getFilteredImages();
    }, [imageFilters]);

    useEffect(() => {
        if (verifiedImages?.length === 0) {
            onRemoveSelectedImage();
            onResetCheckedImages();
        }
    }, [verifiedImages, onRemoveSelectedImage, selected, onResetCheckedImages]);

    const fetchImageData = (searchValue: string | null = "") => {
        const status = tabType === ImagePermissionType.BRAND ? CustomizationImageStatus.BRANDAPPROVED : undefined;
        fetchImages(merchantId, searchValue, imageFilters, status);
    };

    const pageStart = currentPage * EXISTING_IMAGES_PAGE_SIZE;
    const pageEnd = pageStart + EXISTING_IMAGES_PAGE_SIZE;
    const canGoBack = pageStart > 0;
    const canGoForward = pageEnd < images.length;

    useEffect(() => {
        const currentPageItems = images.slice(pageStart, pageEnd);
        setCurrentPageImages(currentPageItems);

        if (currentPageItems[currentPageItems.length - 1] === null) {
            fetchImageData(debouncedSearchValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images, currentPage, debouncedSearchValue]);

    useEffect(() => {
        const tabId = getMerchantId(tabType, merchantId);
        handleTabChange(tabId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabType]);

    useEffect(() => {
        const image = currentPageImages.find((image) => image?.id === selected?.value);
        onSelectImage(image ?? null);
    }, [currentPageImages, selected, onSelectImage]);

    useSkipFirstEffect(() => {
        saveSearchString(debouncedSearchValue);
        resetImagesList();
        setCurrentPage(0);
    }, [debouncedSearchValue]);

    useEffect(() => {
        const currentMerchantId = getMerchantId(tabType, merchantId);
        if (!currentMerchantId || !currentTabCacheKey.includes(currentMerchantId)) {
            return;
        }

        const searchString = getSearchString();
        setSearchValue(searchString);
        setSearchExpanded(!!searchString);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTabCacheKey]);

    useEffect(() => {
        if (!isUploading) {
            return;
        }

        setCurrentPage(0);
        if (debouncedSearchValue) {
            setSearchValue("");
        }
    }, [isUploading, debouncedSearchValue, setSearchValue]);

    const handleExpandSearch = () => setSearchExpanded((searchExpanded) => !searchExpanded);

    const hoveredImage = useMemo(
        () => currentPageImages.find((image) => image?.id === anchorEl?.getAttribute("data-image-id")),
        [currentPageImages, anchorEl]
    );

    const handleNextPage = () => canGoForward && setCurrentPage(currentPage + 1);

    const handlePrevPage = () => canGoBack && setCurrentPage(currentPage - 1);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => setAnchorEl(event.currentTarget);

    const handlePopoverClose = () => setAnchorEl(null);
    const onSearchingStart = () => onSearchingStatusChanged(true);
    const onSearchingEnd = () => onSearchingStatusChanged(false);

    const hasImages = images.length > 0;

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchValue(value);
    };

    const handleExistingImageClick = (event: SyntheticEvent<HTMLElement>) => {
        const imageId = event.currentTarget
            ?.closest(`[data-image-test="${DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_EXISTING}"]`)
            ?.getAttribute("data-image-id");

        if (!imageId) {
            return;
        }

        const existingImage = currentPageImages.find((img) => img?.id === imageId);
        if (!existingImage) {
            return;
        }

        onChange(existingImage);
        handlePopoverClose();
    };

    const circularProgress = () => (
        <div className={classes.circularProgressContainer} data-test={DATATEST.CUSTOMIZATIONS.IMAGE_LIST_SPINNER}>
            <CircularProgress size={46} color="inherit" />
        </div>
    );

    return (
        <>
            {isFetchingExistingImages && !searchValue && circularProgress()}
            {(hasImages || searchValue) && (
                <div data-test={DATATEST.CUSTOMIZATIONS.EXISTING_IMAGE_LIST_CONTAINER}>
                    {hasImages && (
                        <div className={classes.imageListLabel}>
                            {format("List of available images: (Showing |start|-|end|):", {
                                start: pageStart + 1,
                                end: pageEnd,
                            })}
                        </div>
                    )}
                    {images.length === 0 && searchValue && (
                        <div className={classes.imageListLabel}>
                            {format("List of available images: no images fit your search criteria")}
                        </div>
                    )}

                    <div className={classes.searchWrapper}>
                        <div
                            data-test={DATATEST.CUSTOMIZATIONS.EXPAND_IMAGE_SEARCH}
                            className={classes.imageSearchLabel}
                            onClick={handleExpandSearch}
                        >
                            {format("Search Logos:")}
                        </div>
                        <Collapse in={searchExpanded} timeout="auto" unmountOnExit>
                            <FormControl fullWidth className={classes.searchImage} variant="outlined">
                                <OutlinedInput
                                    classes={searchInputClasses as Partial<ClassNameMap<OutlinedInputClassKey>>}
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    placeholder={format("Search")}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchIcon className={classes.searchIcon} />
                                        </InputAdornment>
                                    }
                                    inputProps={{
                                        onBlur: onSearchingEnd,
                                        onFocus: onSearchingStart,
                                    }}
                                />
                            </FormControl>
                        </Collapse>
                    </div>
                    {imagesError && (
                        <div data-test={DATATEST.CUSTOMIZATIONS.IMAGES_ERROR} className={classes.imageListError}>
                            {String(imagesError)}
                        </div>
                    )}
                    {isFetchingExistingImages && !hasImages && searchValue && circularProgress()}
                    {hasImages && (
                        <div>
                            <div className={classes.imagesWithControls}>
                                <div
                                    data-test={DATATEST.CUSTOMIZATIONS.EXISTING_IMAGE_LIST_PAGINATION_PREV}
                                    className={classnames(
                                        !canGoBack ? [classes.paginationButton, "disabled"] : classes.paginationButton
                                    )}
                                    onClick={handlePrevPage}
                                >
                                    <ChevronLeftIcon />
                                </div>
                                <div className={classes.existingImagesContainer}>
                                    {isFetchingExistingImages ? (
                                        <div className={classes.imageListLoadingContainer}>{format("Loading...")}</div>
                                    ) : (
                                        <div
                                            className={classes.existingImagesGrid}
                                            data-test={`${DATATEST.CUSTOMIZATIONS.IMAGE_EXISTING_CONTAINER}`}
                                        >
                                            {currentPageImages.map((image, index) =>
                                                !image ? null : (
                                                    <div
                                                        key={`${image.id}_${index}`}
                                                        className={classnames(
                                                            selected?.value === image.id
                                                                ? [classes.uploadedImageWrapper, "active"]
                                                                : classes.uploadedImageWrapper
                                                        )}
                                                    >
                                                        <div
                                                            onMouseEnter={handlePopoverOpen}
                                                            onMouseLeave={handlePopoverClose}
                                                            onClick={handleExistingImageClick}
                                                            data-image-id={image.id}
                                                            data-test={`${DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_EXISTING}_${image.id}`}
                                                            data-image-test={`${DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_EXISTING}`}
                                                            className={classes.uploadedImage}
                                                        >
                                                            <DropShadow shadowColor="gray" shadowRadius="1px">
                                                                <AspectImage resizeMode="aspectFit" src={image.url} />
                                                            </DropShadow>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div
                                    data-test={DATATEST.CUSTOMIZATIONS.EXISTING_IMAGE_LIST_PAGINATION_NEXT}
                                    className={classnames(
                                        !canGoForward
                                            ? [classes.paginationButton, "disabled"]
                                            : classes.paginationButton
                                    )}
                                    onClick={handleNextPage}
                                >
                                    <ChevronRightIcon />
                                </div>
                                <Popover
                                    style={{ pointerEvents: "none" }}
                                    className={classes.existingImagePopover}
                                    open={Boolean(anchorEl)}
                                    anchorEl={anchorEl}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                    transformOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    onClose={handlePopoverClose}
                                    disableRestoreFocus
                                >
                                    <div className={classes.popoverContentWrapper}>
                                        <div className={classes.popoverImage}>
                                            <DropShadow shadowColor="gray" shadowRadius={"3px"}>
                                                <AspectImage
                                                    resizeMode="aspectFit"
                                                    src={hoveredImage?.url}
                                                    data-test={DATATEST.CUSTOMIZATIONS.POPOVER_IMAGE}
                                                />
                                            </DropShadow>
                                        </div>

                                        <div
                                            data-test={DATATEST.CUSTOMIZATIONS.POPOVER_IMAGE_NAME}
                                            className={classes.popoverImageName}
                                        >
                                            {hoveredImage?.name}
                                        </div>
                                        {hoveredImage?.customizationAttributes
                                            ?.filter(({ isVisible }) => isVisible)
                                            .map((attribute, index) => (
                                                <div
                                                    key={attribute.attributeId}
                                                    className={classes.popoverImageAttribute}
                                                    data-test={`${DATATEST.CUSTOMIZATIONS.POPOVER_IMAGE_ATTRIBUTE}_${index}`}
                                                >
                                                    <span className={classes.popoverImageAttributeName}>
                                                        {`${attribute.name}: `}
                                                    </span>
                                                    <span>{attribute.value}</span>
                                                </div>
                                            ))}
                                    </div>
                                </Popover>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

const getMerchantId = (tabType: ImagePermissionType, merchantId?: string): string | undefined => {
    return tabType === ImagePermissionType.BUYER ? merchantId : ImagePermissionType[tabType];
};

const mapStateToProps = (state: IStore): IStateProps => {
    const { imageUpload, existingImages, company, checkedImages, imagesTab } = state.customization;
    return {
        isUploading: imageUpload?.isUploading,
        isFetchingExistingImages: existingImages?.isFetching,
        imagesError: existingImages?.error,
        tags: state.scope?.brand?.settings?.customizations_image_tag_schemas?.[0]?.tags,
        company: company?.payload,
        isChecking: checkedImages?.isChecking,
        verifiedImages: checkedImages?.verifiedImages,
        checkError: checkedImages?.error,
        isTabChecking: imagesTab?.isChecking,
    };
};

const mapDispatchToProps: MapDispatchToPropsParam<IDispatchProps, IProps> = (dispatch) => ({
    onSearchingStatusChanged: (value: boolean) => dispatch(setSearching(value)),
    onResetCheckedImages: () => dispatch(resetCustomizationCheckedImages()),
});

export const ImagesList = connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps
)(ImagesListContainer);

const useStyles = makeStyles({
    imagesWithControls: {
        display: "flex",
        alignItems: "center",
        width: "100%",
    },
    uploadedImageWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 74,
        height: 74,
        borderRadius: 4,
        "&.active": {
            border: "2px solid #00a9a4",
        },
    },
    uploadedImage: {
        width: 62,
        height: 62,
        borderRadius: 4,
        border: "1px solid #e1e1e1",
        "&:hover": {
            cursor: "pointer",
        },
        display: "flex",
        justifyContent: "center",
    },
    existingImagesContainer: {
        width: "100%",
        justifyContent: "center",
    },

    imageListLoadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    existingImagesGrid: {
        display: "grid",
        flex: 1,
        gridTemplateColumns: "repeat(auto-fit, 74px)",
        gridGap: 20,
        justifyContent: "flex-start",
    },

    paginationButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "24px",
        height: "24px",
        cursor: "pointer",
        flex: "0 0 60px",
        "&.disabled": {
            opacity: "30%",
            cursor: "unset",
        },
    },
    popoverContentWrapper: {
        padding: 12,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    popoverImage: {
        display: "flex",
        justifyContent: "center",
        width: 300,
        height: 300,
        border: "1px solid #e1e1e1",
    },
    popoverImageName: {
        marginTop: 8,
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
    },
    popoverImageAttribute: {
        marginTop: 4,
        fontSize: 10,
        textAlign: "left",
    },
    popoverImageAttributeName: {
        fontWeight: "bold",
    },
    popoverPrice: { color: "#00a9a4" },
    searchImage: {
        "& .MuiInputBase-input": {
            border: "none",
            fontSize: "16px",
        },
        "& .MuiInputAdornment-root": {
            marginRight: "0",
        },
    },
    imageListError: {
        height: "50px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    existingImagePopover: {
        "&.MuiPopover-root": {
            zIndex: "1600 !important",
        },
    },
    imageListLabel: {
        color: theme.palette.textColor,
        fontSize: 12,
        fontWeight: 700,
        marginBlock: "16px 8px",
    },
    searchWrapper: {
        marginBottom: "12px",
        width: "60%",
    },
    searchIcon: {
        fontSize: "20px",
        color: theme.palette.grey,
    },
    imageSearchLabel: {
        color: theme.palette.textColor,
        fontSize: 12,
        fontWeight: 700,
        marginBlock: "16px 8px",
        cursor: "pointer",
        textDecoration: "underline",
    },
    circularProgressContainer: {
        width: "100%",
        alignItems: "center",
        height: "25vh",
        display: "flex",
        justifyContent: "center",
        color: theme.palette.primary1Color,
    },
});

const useSearchInputStyles = makeStyles({
    root: {
        "& $notchedOutline": {
            borderColor: "#e1e1e1",
        },
        "&:hover $notchedOutline": {
            borderColor: "#e1e1e1",
        },
        "&$focused $notchedOutline": {
            borderColor: "#639cd2",
            borderWidth: "1px",
        },
    },
    focused: {},
    notchedOutline: {},
});
